import { animate, state, style, transition, trigger } from '@angular/animations';
import { Component,    EventEmitter, Input, OnInit, Output,  ViewChild, ViewContainerRef} from '@angular/core';
import { DialogResult } from '@progress/kendo-angular-dialog';
import { FileInfo, FileState, RemoveEvent, SelectEvent, UploadComponent, UploadEvent } from '@progress/kendo-angular-upload';
import { TranslateService } from '@ngx-translate/core';
import { AuthService } from 'src/app/core/authentication/auth.service';
import { ConfigService } from 'src/app/core/services/config.service';
import { CustomDialogSettings } from 'src/app/shared/model/CustomDialogSettings';
import { CustomDialogService } from 'src/app/shared/services/custom-dialog.service';
import { FileTypeService } from 'src/app/shared/services/file-type.service';
import { FormUtilsService } from 'src/app/shared/services/form-utils.service';
import { UploadFilesSettings } from '../../models/UploadFilesSettings';
import { Constants } from 'src/app/shared/model/Constants';
import { ImageCompressionService } from 'src/app/shared/services/image-compression.service';

@Component({
  selector: 'app-upload-files',
  templateUrl: './upload-files.component.html',
  styleUrls: ['./upload-files.component.scss'],
  animations: [
    trigger('fadeInOut', [
      state('void', style({
        opacity: 0
      })),
      transition('void <=> *', animate(600)),
    ]),
  ]
})

export class UploadFilesComponent implements OnInit {

  constructor(private configService: ConfigService,
    private auth: AuthService,
    private translateService: TranslateService,
    private formUtilsService: FormUtilsService,
    private fileTypeService: FileTypeService,
    private customDialogService:CustomDialogService,
    private imageCompressionService: ImageCompressionService) {

  }


  @Input() uploadFilesSettings!: UploadFilesSettings;

  @Output() uploadedEvent = new EventEmitter<string>();


  // public onSpecificDataDoneCallback: () => void; //callback on upload control to be called by parent callback on select :)
  uploadDocumentUrl = '';
  uploadControlId = '';
  uploadSelectText = '';
  retryCount  = 0;
  compressionOngoing = false;

  @ViewChild("uploadParamsDialog", { read: ViewContainerRef })
  public containerRef!: ViewContainerRef;

  ngOnInit(): void {
    this.uploadControlId = 'upload_' + this.uploadFilesSettings.uploadDocumentParameters.documentTypeId + '_';
    this.filesToUpload = [];
    this.uploadDocumentUrl = this.configService.PortalWebApiUrl + 'Document';
    this.uploadSelectText = this.uploadFilesSettings.selectButtonTextKey
        ? this.translateService.instant(this.uploadFilesSettings.selectButtonTextKey)
        : this.translateService.instant('kendo.upload.select');

  }
  filesToUpload: Array<any> = [];
  @ViewChild('uploadCtrl') uploadCtrl!: UploadComponent;

  public showButton(state: FileState): boolean {
    return (state === FileState.Uploaded) ? true : false;
  }


  clearFiles() {
    this.uploadCtrl.clearFiles();
    this.filesToUpload = [];
  }

  retryUploadedFiles() {
    const uploadedFiles = this.uploadCtrl.fileList.getFilesWithState(FileState.Uploaded);
    if(uploadedFiles?.length > 0){
      uploadedFiles.forEach( (f) => {
        if (f.uid) {
          this.uploadCtrl.fileList.remove(f.uid);
          this.filesToUpload = this.filesToUpload.filter(x => x.uid !== f.uid);
        }
      });
    }

    const failedFiles = this.uploadCtrl.fileList.getFilesWithState(FileState.Failed);
    if(failedFiles?.length > 0 && this.retryCount < Constants.MaxRetryFileUpload){
      this.retryCount++;
      failedFiles.forEach((f) => {
        if (f.uid) {
          this.uploadCtrl.retryUploadByUid(f.uid);
        }
      });
    }
    
  }

  public onUploadEvent(ev: UploadEvent): void {

    ev.data = {...this.uploadFilesSettings.uploadDocumentParameters};

    ev.headers = ev.headers.append('Authorization', 'Bearer ' + this.auth.getToken());

    ev.files.forEach((file: FileInfo) => {

      const f = this.filesToUpload.filter(f => f.uid == file.uid);
      f.map((x) => {
        
        x.state = 3;
        if (ev.data && !!x.uploadParameters) {
          ev.data = x.uploadParameters;
        }
      });
    });
  }

  public onRemoveEvent(ev: RemoveEvent): void {

    if(!ev.files){
      return;
    }

    ev.files.forEach((file: FileInfo) => {
      this.filesToUpload = this.filesToUpload.filter(f => f.uid !== file.uid);
    });
  }

  public onCompleteEvent(ev: any){
    this.retryUploadedFiles();
   
    this.uploadedEvent.emit("OK")
    
  }

  public onSelectEvent(ev: SelectEvent): void {
    if (this.filesToUpload && ev.files) {
      this.retryCount = 0;
      if (this.uploadFilesSettings.showDocumentDataDialog && !!this.uploadFilesSettings.documentDataDialogContent?.content) {

        const fileUploadDocumentParameters = {};
        this.formUtilsService.mergeObjects(fileUploadDocumentParameters, this.uploadFilesSettings.uploadDocumentParameters)

        const dialogSettings: CustomDialogSettings = {
          content: this.uploadFilesSettings.documentDataDialogContent?.content,
          minWidth: '40vw',
          minHeight: '40vh',
          appendTo: this.containerRef,
          dialogInputs: {
            uploadDocumentParameters: fileUploadDocumentParameters
          },
          resultCallback: (r: DialogResult) => {
            const dialogResult = r as { uploadDocumentParameters?: unknown };

            if(!dialogResult.uploadDocumentParameters){
              ev.files.forEach((x) => {
                if (x.uid) {
                  this.uploadCtrl.removeFilesByUid(x.uid);
                }
              });
              return;
            }

            this.selectDocuments(ev, dialogResult.uploadDocumentParameters);
          }
        }
    
        this.customDialogService.showDialog(dialogSettings);
      }
      else {
        this.selectDocuments(ev, this.uploadFilesSettings.uploadDocumentParameters);
      }
    }
  }

  selectDocuments(ev: SelectEvent, uploadParameters: unknown): void {
    this.compressionOngoing = true;
    const compressionPromises: Promise<void>[] = [];

    ev.files.forEach((file: FileInfo) => {
      const extension = file.extension || '';

      if (file.rawFile && !file.validationErrors) {

        if (this.fileTypeService.isFileImage(extension)) {
       
          if (this.configService.enableFrontendImageCompression) {
            const promise = this.imageCompressionService.compress(file.rawFile).then((newFile) => {
              file.rawFile = newFile;
              file.size = newFile.size;
            });
            
            compressionPromises.push(promise);
          }

          this.filesToUpload.push({ ...file, src: URL.createObjectURL(file.rawFile), isImg: true, state: 1,uploadParameters:uploadParameters });
        }
        else {
          this.filesToUpload.push({ ...file, src: this.fileTypeService.getFileTypeIcon(extension), isImg: false, state: 1 , uploadParameters:uploadParameters});
        }
      }
    });

    Promise.all(compressionPromises).then(() => { 
      this.compressionOngoing = false; 
    });
  }

  public uploadClick(): void {
    this.uploadCtrl.uploadFiles();
  }

  public clearClick(): void {
    this.clearFiles();
  }
}
